/*
 * Add to cart - instant feedback + navigation watchdog
 * ----------------------------------------------------
 * WHY: cart page mode replaced the slide-out drawer with a redirect to /cart.
 * A redirect gives no visual response at the moment of the click, so on a slow
 * connection the button looks dead for several seconds and shoppers report
 * "Add to cart does nothing" and tap again. Testing confirmed the item IS
 * added and the redirect DOES happen - it is the silence in between that reads
 * as breakage.
 *
 * WHAT:
 *   1. Immediate feedback - a thin progress bar plus a busy state on the
 *      button, applied synchronously on submit so there is zero dead time.
 *   2. Watchdog - if nothing has navigated after NAV_DELAY, this navigates to
 *      /cart itself. That covers any product template whose script never opens
 *      the drawer (and therefore never trips the page-mode redirect).
 *
 * SAFETY:
 *   - Never calls preventDefault, so the theme's own submit path is untouched.
 *   - If /cart/add.js returns an error (sold out, inventory), the watchdog is
 *     cancelled so the shopper stays put and sees the theme's error message.
 *   - Payment / GoKwik express buttons are ignored, and an open GoKwik modal
 *     cancels the watchdog, so express checkout is never interrupted.
 *
 * 2026-08-28 - ADD TO CART NO LONGER LEAVES THE PAGE
 *   The watchdog used to cancel only when the add FAILED. On success it let the
 *   timer run, so three seconds after any add the shopper was pulled to /cart -
 *   even though tradeuno-cart-page-mode.js had already performed the add over
 *   fetch and repainted the badge. Two scripts, one adding silently and one
 *   navigating on top of it.
 *
 *   The watchdog is now cancelled on ANY completed /cart/add response, success
 *   or failure. This covers every add path in the theme at once - the bag icon
 *   on product cards, quick add, quick view, the PDP button and the sticky
 *   bottom bar - because they all post to /cart/add. The item is added, the
 *   header count updates, the shopper stays where they are.
 *
 *   The timer still fires when NO /cart/add request is seen at all, which is the
 *   case this watchdog was written for - a genuine native form post that nothing
 *   intercepted. That safety net is unchanged.
 *
 * 2026-09-24 - "ITEM ADDED TO YOUR CART" POPUP
 *   Staying on the page after an add left the small header count as the only
 *   confirmation, which shoppers miss. Every successful /cart/add now shows a
 *   green popup ("Item added to your cart") in the top-right corner, full
 *   width at the top on phones. It closes on its own after TOAST_MS or with
 *   the X. Same hook as the watchdog, so every add path gets it: product cards,
 *   quick add, quick view, the PDP button and the sticky bottom bar.
 *   Not shown for GoKwik Buy It Now - that goes straight to checkout, so a
 *   "cart" message there would be wrong. Failed adds show no popup; the
 *   theme's own error message stays in charge.
 *
 * TO REVERT: delete the <script> tag for this file from layout/theme.liquid.
 */
(function () {
  var NAV_DELAY = 3000;
  var BAR_ID = 'tu-atc-progress';
  var STYLE_ID = 'tu-atc-progress-style';

  var EXPRESS_SELECTOR =
    '.shopify-payment-button, #gokwik-buy-now, .gokwik-checkout, [id*="gokwik"], [class*="gokwik"]';

  var TOAST_ID = 'tu-atc-toast';
  var TOAST_MS = 3500;
  var BUY_NOW_WINDOW_MS = 4000;

  var timer = null;
  var busyButton = null;
  var failed = false;
  var toastTimer = null;
  var lastExpressClickAt = 0;

  function cartUrl() {
    var base = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        base = window.Shopify.routes.root;
      }
    } catch (e) {}
    if (base.charAt(base.length - 1) === '/') base = base.slice(0, -1);
    return base + '/cart';
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '#' + BAR_ID + '{position:fixed;top:0;left:0;height:3px;width:0;z-index:2147483000;' +
      'background:#C5A02B;transition:width .45s ease-out;pointer-events:none}' +
      '[data-tu-atc-busy]{opacity:.75;cursor:progress}' +
      '#' + TOAST_ID + '{position:fixed;top:20px;right:20px;z-index:2147483000;display:flex;align-items:center;' +
      'gap:12px;min-width:300px;max-width:calc(100vw - 40px);padding:14px 16px;background:#0A8A2E;color:#fff;' +
      'border-radius:4px;box-shadow:0 6px 20px rgba(0,0,0,.18);font-size:15px;font-weight:500;line-height:1.3;' +
      'opacity:0;transform:translateY(-12px);transition:opacity .25s ease,transform .25s ease;pointer-events:none}' +
      '#' + TOAST_ID + '.is-visible{opacity:1;transform:none;pointer-events:auto}' +
      '#' + TOAST_ID + ' .tu-atc-toast__msg{flex:1}' +
      '#' + TOAST_ID + ' svg{flex-shrink:0;display:block}' +
      '#' + TOAST_ID + ' .tu-atc-toast__close{background:none;border:0;padding:4px;margin:-4px;color:#fff;cursor:pointer;line-height:0}' +
      '@media (max-width:767px){#' + TOAST_ID + '{top:0;left:0;right:0;min-width:0;max-width:none;border-radius:0;' +
      'padding-top:calc(14px + env(safe-area-inset-top,0px))}}' +
      '@media (prefers-reduced-motion:reduce){#' + TOAST_ID + '{transition:none}}';
    document.head.appendChild(s);
  }

  function showBar() {
    injectStyles();
    var bar = document.getElementById(BAR_ID);
    if (!bar) {
      bar = document.createElement('div');
      bar.id = BAR_ID;
      document.body.appendChild(bar);
    }
    bar.style.width = '0';
    // Force a reflow so the transition runs from 0.
    void bar.offsetWidth;
    bar.style.width = '75%';
  }

  function hideBar() {
    var bar = document.getElementById(BAR_ID);
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
  }

  function hideToast() {
    var t = document.getElementById(TOAST_ID);
    if (toastTimer) {
      window.clearTimeout(toastTimer);
      toastTimer = null;
    }
    if (t) t.classList.remove('is-visible');
  }

  function showToast() {
    injectStyles();
    var t = document.getElementById(TOAST_ID);
    if (!t) {
      t = document.createElement('div');
      t.id = TOAST_ID;
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      t.innerHTML =
        '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
        '<span class="tu-atc-toast__msg">Item added to your cart</span>' +
        '<button type="button" class="tu-atc-toast__close" aria-label="Close">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
        'stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line></svg>' +
        '</button>';
      t.querySelector('.tu-atc-toast__close').addEventListener('click', hideToast);
      document.body.appendChild(t);
    }
    // Restart cleanly if a second add lands while the popup is still up.
    t.classList.remove('is-visible');
    void t.offsetWidth;
    t.classList.add('is-visible');
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, TOAST_MS);
  }

  function fromBuyNow() {
    return Date.now() - lastExpressClickAt < BUY_NOW_WINDOW_MS || gokwikOpen();
  }

  function setBusy(btn, on) {
    if (!btn) return;
    if (on) {
      btn.setAttribute('data-tu-atc-busy', '');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('data-tu-atc-busy');
      btn.removeAttribute('aria-busy');
    }
  }

  function gokwikOpen() {
    var m = document.querySelector('#gokwik-modal-wrapper, .gk-iframe');
    if (!m) return false;
    var cs = window.getComputedStyle(m);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  function cancel() {
    if (timer) {
      window.clearTimeout(timer);
      timer = null;
    }
    hideBar();
    setBusy(busyButton, false);
    busyButton = null;
  }

  function go() {
    timer = null;
    if (failed || gokwikOpen()) {
      cancel();
      return;
    }
    var url = cartUrl();
    if (window.location.pathname !== url) {
      window.location.href = url;
    } else {
      cancel();
    }
  }

  // Watch the add request. A completed add - success or failure - means the
  // shopper stays put, so the watchdog is stood down either way. On failure the
  // theme shows its own error; on success the header count is the confirmation.
  var originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (input) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {}

      var promise = originalFetch.apply(this, arguments);

      if (url.indexOf('/cart/add') !== -1) {
        promise
          .then(function (res) {
            if (!res || !res.ok) failed = true;
            cancel();
            if (res && res.ok && !fromBuyNow()) showToast();
          })
          .catch(function () {
            failed = true;
            cancel();
          });
      }

      return promise;
    };
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || typeof form.getAttribute !== 'function') return;

      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      var submitter = e.submitter || null;
      if (submitter && submitter.closest && submitter.closest(EXPRESS_SELECTOR)) return;
      if (gokwikOpen()) return;

      failed = false;
      busyButton = submitter || form.querySelector('[type="submit"]');

      setBusy(busyButton, true);
      showBar();

      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(go, NAV_DELAY);
    },
    true
  );

  // Remember express-checkout clicks so their cart adds don't show the popup.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (t && typeof t.closest === 'function' && t.closest(EXPRESS_SELECTOR)) {
        lastExpressClickAt = Date.now();
      }
    },
    true
  );

  // If the page is restored from bfcache, clear any stale busy state.
  window.addEventListener('pageshow', cancel);
})();
